import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash, Plus } from './icons/GeistIcons';
import '../styles/WorkHistoryEditor.css';

const currentYear = new Date().getFullYear();
const MIN_YEAR = 1950;
const MAX_START_YEAR = currentYear + 1;
const MAX_END_YEAR = currentYear;

const WorkHistoryEditor = ({ value = [], onChange, onValidationChange }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [errors, setErrors] = useState({});

  const validateEntry = useCallback((entry) => {
    const entryErrors = {};
    
    // Check if entry has any meaningful content
    const hasAnyContent = (entry.role && entry.role.trim() !== '') || 
                          (entry.company && entry.company.trim() !== '') || 
                          entry.startDate || 
                          entry.endDate || 
                          entry.isCurrent;
    
    // Only validate if entry has some content (partial entries must be complete)
    // Empty entries will be filtered out at save time
    if (hasAnyContent) {
      if (!entry.role || entry.role.trim() === '') {
        entryErrors.role = t('tutorProfile.validation.roleRequired', 'Role is required');
      }
      if (!entry.startDate || entry.startDate === '') {
        entryErrors.startDate = t('tutorProfile.validation.startDateRequired', 'Start year is required');
      } else {
        const startYear = parseInt(entry.startDate, 10);
        if (isNaN(startYear) || startYear < MIN_YEAR || startYear > MAX_START_YEAR) {
          entryErrors.startDate = t('tutorProfile.validation.invalidYear', 'Year must be between {{min}} and {{max}}', { min: MIN_YEAR, max: MAX_START_YEAR });
        }
      }
      // End year is required unless "Present" is checked
      if (!entry.isCurrent) {
        if (!entry.endDate || entry.endDate === '') {
          entryErrors.endDate = t('tutorProfile.validation.endDateRequired', 'End year is required (or check Present)');
        } else {
          const endYear = parseInt(entry.endDate, 10);
          const startYear = parseInt(entry.startDate, 10);
          if (isNaN(endYear) || endYear < MIN_YEAR || endYear > MAX_END_YEAR) {
            entryErrors.endDate = t('tutorProfile.validation.invalidYear', 'Year must be between {{min}} and {{max}}', { min: MIN_YEAR, max: MAX_END_YEAR });
          } else if (!isNaN(startYear) && endYear < startYear) {
            entryErrors.endDate = t('tutorProfile.validation.endBeforeStart', 'End year cannot be before start year');
          }
        }
      }
    }
    return entryErrors;
  }, [t]);

  const validateAllEntries = useCallback((entriesToValidate) => {
    const newErrors = {};
    entriesToValidate.forEach(entry => {
      const entryErrors = validateEntry(entry);
      if (Object.keys(entryErrors).length > 0) {
        newErrors[entry.id] = entryErrors;
      }
    });
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    // Notify parent of validation state
    if (onValidationChange) {
      onValidationChange(isValid);
    }
    return isValid;
  }, [validateEntry, onValidationChange]);

  // Only sync from parent when value reference changes (not on every render)
  const valueRef = React.useRef(value);
  const isInitialMount = React.useRef(true);
  
  useEffect(() => {
    // On initial mount, always process the value
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // Skip if value hasn't actually changed (shallow compare by reference)
      if (valueRef.current === value) return;
    }
    valueRef.current = value;
    
    // Normalize input value to array of objects
    const normalized = Array.isArray(value) ? value.map(item => {
      if (typeof item === 'string') {
        return {
          id: Math.random().toString(36).substr(2, 9),
          role: item,
          company: '',
          startDate: '',
          endDate: '',
          isCurrent: false,
          description: ''
        };
      }
      return {
        id: item.id || Math.random().toString(36).substr(2, 9),
        role: item.role || '',
        company: item.company || '',
        startDate: item.startDate || '',
        endDate: item.endDate || '',
        isCurrent: item.isCurrent || false,
        description: item.description || ''
      };
    }) : [];
    
    setEntries(normalized);
    // Validate loaded entries
    validateAllEntries(normalized);
  }, [value, validateAllEntries]);

  const handleChange = (id, field, newValue) => {
    const newEntries = entries.map(entry => {
      if (entry.id === id) {
        const updated = { ...entry, [field]: newValue };
        // Clear endDate if isCurrent is toggled on
        if (field === 'isCurrent' && newValue === true) {
          updated.endDate = '';
        }
        return updated;
      }
      return entry;
    });
    setEntries(newEntries);
    validateAllEntries(newEntries);
    
    // Pass all entries to parent - filtering happens at save time in TutorProfile
    onChange(newEntries);
  };

  const handleAdd = () => {
    const newEntry = {
      id: Math.random().toString(36).substr(2, 9),
      role: '',
      company: '',
      startDate: '',
      endDate: '',
      isCurrent: false,
      description: ''
    };
    const newEntries = [...entries, newEntry];
    setEntries(newEntries);
    // Don't validate or call onChange for completely empty entry
    // It will be validated once user starts typing
  };

  const handleRemove = (id) => {
    const newEntries = entries.filter(e => e.id !== id);
    setEntries(newEntries);
    // Clear errors for removed entry
    const newErrors = { ...errors };
    delete newErrors[id];
    setErrors(newErrors);
    
    // Pass all entries to parent - filtering happens at save time in TutorProfile
    onChange(newEntries);
  };

  const getFieldError = (entryId, field) => {
    return errors[entryId]?.[field];
  };

  const hasError = (entryId, field) => {
    return !!getFieldError(entryId, field);
  };

  return (
    <div className="work-history-editor">
      {entries.map((entry, index) => (
        <div key={entry.id} className="work-history-item">
          <div className="work-history-header">
            <span className="work-history-index">#{index + 1}</span>
            <button 
              type="button" 
              className="work-history-remove-btn"
              onClick={() => handleRemove(entry.id)}
              aria-label="Remove entry"
            >
              <Trash size={16} />
            </button>
          </div>
          
          <div className="work-history-grid">
            <div className="form-group">
              <label className="sub-label">
                {t('tutorProfile.role', 'Role / Job Title')}
                <span className="required-mark">*</span>
              </label>
              <input
                type="text"
                className={`work-history-input ${hasError(entry.id, 'role') ? 'has-error' : ''}`}
                value={entry.role}
                onChange={(e) => handleChange(entry.id, 'role', e.target.value)}
                placeholder="e.g. Senior English Teacher"
              />
              {hasError(entry.id, 'role') && (
                <span className="field-error">{getFieldError(entry.id, 'role')}</span>
              )}
            </div>
            
            <div className="form-group">
              <label className="sub-label">{t('tutorProfile.company', 'Company / School')}</label>
              <input
                type="text"
                className="work-history-input"
                value={entry.company}
                onChange={(e) => handleChange(entry.id, 'company', e.target.value)}
                placeholder="e.g. ABC Language Academy"
              />
            </div>

            <div className="form-group">
              <label className="sub-label">
                {t('tutorProfile.startDate', 'Start Year')}
                <span className="required-mark">*</span>
              </label>
              <input
                type="number"
                min={MIN_YEAR}
                max={MAX_START_YEAR}
                placeholder="YYYY"
                className={`work-history-input ${hasError(entry.id, 'startDate') ? 'has-error' : ''}`}
                value={entry.startDate}
                onChange={(e) => handleChange(entry.id, 'startDate', e.target.value)}
              />
              {hasError(entry.id, 'startDate') && (
                <span className="field-error">{getFieldError(entry.id, 'startDate')}</span>
              )}
            </div>

            <div className="form-group">
              <label className="sub-label">
                {t('tutorProfile.endDate', 'End Year')}
                {!entry.isCurrent && <span className="required-mark">*</span>}
              </label>
              <div className="end-date-wrapper">
                <input
                  type="number"
                  min={MIN_YEAR}
                  max={MAX_END_YEAR}
                  placeholder="YYYY"
                  className={`work-history-input ${hasError(entry.id, 'endDate') ? 'has-error' : ''}`}
                  value={entry.endDate}
                  disabled={entry.isCurrent}
                  onChange={(e) => handleChange(entry.id, 'endDate', e.target.value)}
                />
                <button
                  type="button"
                  className={`present-toggle ${entry.isCurrent ? 'is-on' : ''}`}
                  role="switch"
                  aria-checked={entry.isCurrent}
                  onClick={() => handleChange(entry.id, 'isCurrent', !entry.isCurrent)}
                >
                  <span className="present-toggle-track" aria-hidden="true">
                    <span className="present-toggle-thumb" />
                  </span>
                  <span className="present-toggle-label">{t('tutorProfile.current', 'Present')}</span>
                </button>
              </div>
              {hasError(entry.id, 'endDate') && (
                <span className="field-error">{getFieldError(entry.id, 'endDate')}</span>
              )}
            </div>
            
            {/* Optional description field if needed */}
            {/* 
            <div className="form-group full-width">
              <textarea ... />
            </div> 
            */}
          </div>
        </div>
      ))}

      <button type="button" className="work-history-add-btn" onClick={handleAdd}>
        <Plus size={16} />
        <span>{t('tutorProfile.addWorkHistory', 'Add Experience')}</span>
      </button>
    </div>
  );
};

export default WorkHistoryEditor;
